import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { client } from '@/lib/api';
import EmailMessage, { type EmailMessageData } from '@/components/EmailMessage';
import ReplyBox from '@/components/ReplyBox';
import { Card } from '@/components/ui/card';
import { formatInstant } from '@/lib/date';
import { gmailThreadUrl } from '@/lib/gmail-url';

export interface ThreadSummary {
  gmailThreadId: string;
  subject: string | null;
  messageCount: number;
  lastMessageAt: string;
  isOriginal: boolean;
  preview: { direction: string; fromAddress: string | null; snippet: string | null } | null;
  /** null when the server did not fetch this thread's bodies yet — not an
   *  error, and never rendered as one. */
  messages: EmailMessageData[] | null;
}

/**
 * The vendor's conversation, split into the Gmail threads it actually is.
 *
 * A thread the planner has not opened is a row, not a fetch: bodies are live
 * Gmail reads, and the server ships only the newest thread's. Opening an older
 * one pays for it then.
 */
export default function ConversationThreads({
  inquiryId,
  threads,
  expandedThreadId,
  onToggle,
  gmailAddress,
  highlight,
}: {
  inquiryId: string;
  threads: ThreadSummary[];
  expandedThreadId: string | null;
  onToggle: (gmailThreadId: string) => void;
  /** The connected mailbox, for the link out. Null when none is connected. */
  gmailAddress: string | null;
  /** Text to mark in one specific message — the sentence a quote was read
   *  from, when the planner has followed the evidence link. */
  highlight?: { messageId: string; text: string } | null;
}) {
  return (
    // A stack of separate cards rather than one card of rows: two conversations
    // with the same vendor are two objects, and a shared surface with hairlines
    // between them reads as one long email.
    <div className="space-y-3">
      {threads.map((thread) => (
        <ThreadRow
          key={thread.gmailThreadId}
          inquiryId={inquiryId}
          thread={thread}
          expanded={thread.gmailThreadId === expandedThreadId}
          onToggle={onToggle}
          gmailAddress={gmailAddress}
          highlight={highlight}
        />
      ))}
    </div>
  );
}

function ThreadRow({
  inquiryId,
  thread,
  expanded,
  onToggle,
  gmailAddress,
  highlight,
}: {
  inquiryId: string;
  thread: ThreadSummary;
  expanded: boolean;
  onToggle: (gmailThreadId: string) => void;
  gmailAddress: string | null;
  highlight?: { messageId: string; text: string } | null;
}) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['inquiry-thread-bodies', inquiryId, thread.gmailThreadId],
    queryFn: async () => {
      const res = await client.api.inquiries[':id'].threads[':gmailThreadId'].$get({
        param: { id: inquiryId, gmailThreadId: thread.gmailThreadId },
      });
      if (!res.ok) throw new Error('Failed to load the conversation');
      return res.json();
    },
    // The newest thread arrives already rendered; asking for it again would
    // spend a fan-out of Gmail reads on text we are already holding.
    enabled: expanded && thread.messages === null,
    // Same reasoning as the list query's: bodies do not change, and refetching
    // on focus spends quota to redisplay them.
    staleTime: 5 * 60 * 1000,
  });

  const messages = thread.messages ?? data?.messages ?? null;
  const title = thread.subject ?? '(no subject)';

  return (
    <Card className="overflow-hidden rounded-3xl bg-card p-0 shadow-ambient">
      {/* The rule under the header appears only when the thread is open, so a
          collapsed row stays a single quiet line. */}
      <div className={`flex items-center gap-2 px-5 ${expanded ? 'border-b border-border' : ''}`}>
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => onToggle(thread.gmailThreadId)}
          className="flex min-w-0 flex-1 items-center gap-2 py-4 text-left"
        >
          {expanded ? (
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          )}
          <span className="truncate text-sm font-medium">{title}</span>
          {thread.isOriginal && (
            <span className="label-caps shrink-0 text-xs text-muted-foreground">
              Original inquiry
            </span>
          )}
          <span className="ml-auto shrink-0 text-xs text-muted-foreground">
            {thread.messageCount} {thread.messageCount === 1 ? 'msg' : 'msgs'} ·{' '}
            {formatInstant(thread.lastMessageAt, { month: 'short', day: 'numeric' })}
          </span>
        </button>
        {/* A sibling of the toggle, not a child: a link inside a button is
            neither reliably clickable nor announced as a link. */}
        {gmailAddress && (
          <a
            href={gmailThreadUrl(gmailAddress, thread.gmailThreadId)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open in Gmail: ${title}`}
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <ExternalLink className="size-4" />
          </a>
        )}
      </div>

      {!expanded && thread.preview?.snippet && (
        <p className="truncate px-5 pb-4 pl-11 text-xs text-muted-foreground">
          {thread.preview.snippet}
        </p>
      )}

      {expanded && (
        <div className="px-5 pb-5">
          {messages ? (
            <>
              <div className="divide-y divide-border">
                {messages.map((m) => (
                  <EmailMessage
                    key={m.id}
                    message={m}
                    threadSubject={thread.subject}
                    highlight={highlight?.messageId === m.id ? highlight.text : undefined}
                    inquiryId={inquiryId}
                  />
                ))}
              </div>
              {/* At the end of the thread, because that is what a reply is: the
                  next message in this conversation. */}
              <ReplyBox inquiryId={inquiryId} threadId={thread.gmailThreadId} />
            </>
          ) : isLoading ? (
            <p className="py-4 text-sm text-muted-foreground">Loading this conversation…</p>
          ) : isError ? (
            // Scoped to this thread: every other thread on the page is still
            // readable, and re-fetching one is cheaper than reloading them all.
            <p className="py-4 text-sm text-muted-foreground">
              This conversation couldn't be loaded from Gmail.{' '}
              <button type="button" onClick={() => refetch()} className="underline">
                Try again
              </button>
            </p>
          ) : null}
        </div>
      )}
    </Card>
  );
}
